import { useState, useEffect, useMemo, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { TokenSearch } from '../components/TokenSearch';
import {
  getNewTokens,
  getGraduatingTokens,
  getTrendingTokens,
  searchTokens,
  getFeedStats,
  type TrendingToken,
  type FeedStats,
  type RiskLevel,
} from '../lib/api';
import { formatMarketCap, formatTimeAgo, formatNumber } from '../lib/utils';
import { usePumpPortal } from '../hooks/usePumpPortal';

type FeedTab = 'trending' | 'new' | 'graduating';
type ViewMode = 'grid' | 'table';

const MARKET_CAP_OPTIONS = [
  { label: 'All', min: 0, max: Infinity },
  { label: '< $10K', min: 0, max: 10000 },
  { label: '$10K-$100K', min: 10000, max: 100000 },
  { label: '$100K-$1M', min: 100000, max: 1000000 },
  { label: '> $1M', min: 1000000, max: Infinity },
];

const AGE_OPTIONS = [
  { label: 'All', hours: Infinity },
  { label: '< 1h', hours: 1 },
  { label: '< 6h', hours: 6 },
  { label: '< 24h', hours: 24 },
];

const RISK_OPTIONS: { label: string; value: RiskLevel }[] = [
  { label: 'Low', value: 'low' },
  { label: 'Medium', value: 'medium' },
  { label: 'High', value: 'high' },
  { label: 'Critical', value: 'critical' },
];

export function Home() {
  const navigate = useNavigate();
  const [searching, setSearching] = useState(false);
  const [activeTab, setActiveTab] = useState<FeedTab>('trending');
  const [viewMode, setViewMode] = useState<ViewMode>('grid');
  const [showFilters, setShowFilters] = useState(false);
  const [heroCollapsed, setHeroCollapsed] = useState(() => {
    try { return localStorage.getItem('skop_hero_collapsed') === '1'; } catch { return false; }
  });

  // Data
  const [trendingTokens, setTrendingTokens] = useState<TrendingToken[]>([]);
  const [newTokens, setNewTokens] = useState<TrendingToken[]>([]);
  const [graduatingTokens, setGraduatingTokens] = useState<(TrendingToken & { bondingProgress: number })[]>([]);
  const [searchResults, setSearchResults] = useState<TrendingToken[]>([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [loadingFeed, setLoadingFeed] = useState(false);
  const [stats, setStats] = useState<FeedStats | null>(null);

  // Filters
  const [marketCapIndex, setMarketCapIndex] = useState(0);
  const [ageIndex, setAgeIndex] = useState(0);
  const [selectedRisks, setSelectedRisks] = useState<RiskLevel[]>([]);
  const [requireSocials, setRequireSocials] = useState(false);
  const [requireLpBurned, setRequireLpBurned] = useState(false);
  const [requireMintRevoked, setRequireMintRevoked] = useState(false);

  // Real-time updates via WebSocket
  const [realtimeEnabled] = useState(true); // TODO: Add toggle in UI

  const handleNewToken = useCallback((token: TrendingToken) => {
    // Add new token to the top of the list (for "new" tab)
    setNewTokens(prev => {
      // Avoid duplicates
      if (prev.some(t => t.address === token.address)) return prev;
      // Keep max 50 tokens, add new one at top
      return [token, ...prev.slice(0, 49)];
    });

    // Update stats
    setStats(prev => prev ? {
      ...prev,
      newTokensLastHour: prev.newTokensLastHour + 1,
    } : prev);
  }, []);

  // Connect to PumpPortal WebSocket
  usePumpPortal({
    onNewToken: handleNewToken,
    enabled: realtimeEnabled,
  });

  const handleSearch = (address: string) => {
    setSearching(true);
    navigate(`/${address}`);
  };

  const handleSearchChange = async (query: string) => {
    setSearchQuery(query);
    if (query.length >= 2) {
      const results = await searchTokens(query);
      setSearchResults(results);
    } else {
      setSearchResults([]);
    }
  };

  // Load stats
  useEffect(() => {
    getFeedStats().then(setStats);
  }, []);

  // Load feeds when tab changes
  useEffect(() => {
    const loadFeed = async () => {
      setLoadingFeed(true);
      try {
        if (activeTab === 'trending' && trendingTokens.length === 0) {
          const tokens = await getTrendingTokens();
          setTrendingTokens(tokens);
        } else if (activeTab === 'new' && newTokens.length === 0) {
          const tokens = await getNewTokens(20);
          setNewTokens(tokens);
        } else if (activeTab === 'graduating' && graduatingTokens.length === 0) {
          const tokens = await getGraduatingTokens(10);
          setGraduatingTokens(tokens);
        }
      } catch (e) {
        console.error('Failed to load feed:', e);
      } finally {
        setLoadingFeed(false);
      }
    };

    loadFeed();
  }, [activeTab, trendingTokens.length, newTokens.length, graduatingTokens.length]);

  // Apply filters
  const filteredTokens = useMemo(() => {
    const sourceTokens = activeTab === 'trending'
      ? trendingTokens
      : activeTab === 'new'
      ? newTokens
      : graduatingTokens;

    return sourceTokens.filter((token) => {
      // Market cap filter
      const mcOption = MARKET_CAP_OPTIONS[marketCapIndex];
      if (token.marketCap !== undefined) {
        if (token.marketCap < mcOption.min || token.marketCap > mcOption.max) {
          return false;
        }
      }

      // Age filter
      const ageOption = AGE_OPTIONS[ageIndex];
      if (token.createdAt && ageOption.hours !== Infinity) {
        const ageHours = (Date.now() - token.createdAt) / (1000 * 60 * 60);
        if (ageHours > ageOption.hours) {
          return false;
        }
      }

      // Risk level filter
      if (selectedRisks.length > 0 && !selectedRisks.includes(token.riskLevel)) {
        return false;
      }

      // Safety filters
      if (requireSocials && !token.hasTwitter && !token.hasTelegram) {
        return false;
      }
      if (requireLpBurned && !token.lpBurned) {
        return false;
      }
      if (requireMintRevoked && !token.mintRevoked) {
        return false;
      }

      return true;
    });
  }, [activeTab, trendingTokens, newTokens, graduatingTokens, marketCapIndex, ageIndex, selectedRisks, requireSocials, requireLpBurned, requireMintRevoked]);

  const activeFilterCount = useMemo(() => {
    let count = 0;
    if (marketCapIndex > 0) count++;
    if (ageIndex > 0) count++;
    if (selectedRisks.length > 0) count++;
    if (requireSocials) count++;
    if (requireLpBurned) count++;
    if (requireMintRevoked) count++;
    return count;
  }, [marketCapIndex, ageIndex, selectedRisks, requireSocials, requireLpBurned, requireMintRevoked]);

  const clearFilters = () => {
    setMarketCapIndex(0);
    setAgeIndex(0);
    setSelectedRisks([]);
    setRequireSocials(false);
    setRequireLpBurned(false);
    setRequireMintRevoked(false);
  };

  const dismissHero = () => {
    setHeroCollapsed(true);
    try { localStorage.setItem('skop_hero_collapsed', '1'); } catch { /* */ }
  };

  return (
    <div style={{ padding: '16px 0' }}>
      {/* Hero / Landing Section */}
      {!heroCollapsed && (
        <div style={{
          position: 'relative',
          marginBottom: '20px',
          padding: '28px 24px 24px',
          background: 'var(--bg-secondary)',
          border: '1px solid var(--border)',
          borderRadius: 'var(--radius-lg)',
        }}>
          <button
            onClick={dismissHero}
            title="Dismiss"
            style={{
              position: 'absolute',
              top: '10px',
              right: '12px',
              background: 'none',
              border: 'none',
              color: 'var(--text-tertiary)',
              fontSize: '16px',
              cursor: 'pointer',
              fontFamily: 'var(--font-mono)',
              lineHeight: 1,
            }}
          >
            x
          </button>

          <div style={{ maxWidth: '640px' }}>
            <h1 style={{
              fontSize: '22px',
              fontWeight: 700,
              letterSpacing: '-0.02em',
              marginBottom: '6px',
            }}>
              Trade across every Solana DEX with built-in risk analysis
            </h1>
            <p style={{
              fontSize: '14px',
              color: 'var(--text-secondary)',
              lineHeight: 1.5,
              marginBottom: '20px',
            }}>
              Buy and sell on pump.fun, PumpSwap, Raydium, Jupiter, and LetsBONK -- all from one place.
              Every token gets a risk score backed by on-chain data you can verify.
            </p>
          </div>

          {/* Feature Pills */}
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))',
            gap: '10px',
          }}>
            <FeaturePill
              icon={<ShieldIcon />}
              title="Risk Detection"
              desc="Bundle, sybil, and wash trade analysis"
            />
            <FeaturePill
              icon={<SimIcon />}
              title="Tx Simulation"
              desc="Preview trades before signing"
            />
            <FeaturePill
              icon={<RadarIcon />}
              title="Real-time Alerts"
              desc="Whale moves and token events"
            />
            <FeaturePill
              icon={<KeyIcon />}
              title="Multi-DEX Trading"
              desc="pump.fun, PumpSwap, Raydium, Jupiter"
            />
          </div>

          {/* Fee note */}
          <div style={{
            marginTop: '16px',
            fontSize: '12px',
            fontFamily: 'var(--font-mono)',
            color: 'var(--text-tertiary)',
            letterSpacing: '0.02em',
          }}>
            1% fee per trade &middot; all features free &middot; no subscriptions
          </div>
        </div>
      )}

      {/* Stats Bar */}
      {stats && (
        <div style={{
          display: 'flex',
          justifyContent: 'center',
          gap: '24px',
          marginBottom: '16px',
          padding: '10px 16px',
          background: 'var(--bg-secondary)',
          borderRadius: 'var(--radius-md)',
          border: '1px solid var(--border)',
        }}>
          <StatItem label="New (1h)" value={stats.newTokensLastHour.toString()} />
          <StatItem label="Volume (24h)" value={`$${formatNumber(stats.totalVolume24h, 1)}`} />
          <StatItem label="Graduating" value={stats.graduatingSoon.toString()} accent />
        </div>
      )}

      {/* Search */}
      <TokenSearch
        onSearch={handleSearch}
        loading={searching}
        onQueryChange={handleSearchChange}
      />

      {/* Search Results Dropdown */}
      {searchResults.length > 0 && searchQuery.length >= 2 && (
        <div style={{
          marginTop: '8px',
          background: 'var(--bg-secondary)',
          border: '1px solid var(--border)',
          borderRadius: 'var(--radius-md)',
          overflow: 'hidden',
          maxHeight: '300px',
          overflowY: 'auto',
        }}>
          {searchResults.map((token) => (
            <SearchResultItem key={token.address} token={token} onClick={() => handleSearch(token.address)} />
          ))}
        </div>
      )}

      {/* Controls: Tabs + Filters + View Toggle */}
      <div style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        marginTop: '20px',
        marginBottom: '12px',
        gap: '12px',
        flexWrap: 'wrap',
      }}>
        {/* Tabs */}
        <div style={{
          display: 'flex',
          gap: '0',
          borderBottom: '1px solid var(--border)',
        }}>
          <TabButton active={activeTab === 'trending'} onClick={() => setActiveTab('trending')}>
            trending
          </TabButton>
          <TabButton active={activeTab === 'new'} onClick={() => setActiveTab('new')}>
            new
          </TabButton>
          <TabButton active={activeTab === 'graduating'} onClick={() => setActiveTab('graduating')}>
            graduating
          </TabButton>
        </div>

        {/* Right controls */}
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          {/* Filter toggle */}
          <button
            onClick={() => setShowFilters(!showFilters)}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '6px',
              padding: '6px 12px',
              background: showFilters ? 'var(--accent-muted)' : 'var(--bg-secondary)',
              border: '1px solid',
              borderColor: showFilters ? 'var(--accent)' : 'var(--border)',
              borderRadius: 'var(--radius-md)',
              color: showFilters ? 'var(--accent)' : 'var(--text-secondary)',
              fontSize: '12px',
              fontFamily: 'var(--font-mono)',
              cursor: 'pointer',
            }}
          >
            <FilterIcon />
            Filters
            {activeFilterCount > 0 && (
              <span style={{
                padding: '1px 6px',
                background: 'var(--accent)',
                color: '#000',
                borderRadius: '10px',
                fontSize: '10px',
                fontWeight: 600,
              }}>
                {activeFilterCount}
              </span>
            )}
          </button>

          {/* View toggle */}
          <div style={{
            display: 'flex',
            border: '1px solid var(--border)',
            borderRadius: 'var(--radius-md)',
            overflow: 'hidden',
          }}>
            <ViewToggleButton active={viewMode === 'grid'} onClick={() => setViewMode('grid')}>
              <GridIcon />
            </ViewToggleButton>
            <ViewToggleButton active={viewMode === 'table'} onClick={() => setViewMode('table')}>
              <TableIcon />
            </ViewToggleButton>
          </div>
        </div>
      </div>

      {/* Filters Panel */}
      {showFilters && (
        <FiltersPanel
          marketCapIndex={marketCapIndex}
          setMarketCapIndex={setMarketCapIndex}
          ageIndex={ageIndex}
          setAgeIndex={setAgeIndex}
          selectedRisks={selectedRisks}
          setSelectedRisks={setSelectedRisks}
          requireSocials={requireSocials}
          setRequireSocials={setRequireSocials}
          requireLpBurned={requireLpBurned}
          setRequireLpBurned={setRequireLpBurned}
          requireMintRevoked={requireMintRevoked}
          setRequireMintRevoked={setRequireMintRevoked}
          onClear={clearFilters}
          activeCount={activeFilterCount}
        />
      )}

      {/* Results count */}
      <div style={{
        fontSize: '11px',
        color: 'var(--text-tertiary)',
        marginBottom: '12px',
        fontFamily: 'var(--font-mono)',
      }}>
        {filteredTokens.length} tokens
        {activeFilterCount > 0 && ` (filtered)`}
      </div>

      {/* Token Display */}
      {loadingFeed ? (
        <LoadingGrid viewMode={viewMode} />
      ) : viewMode === 'grid' ? (
        <TokenGrid
          tokens={filteredTokens}
          onSelect={handleSearch}
          showProgress={activeTab === 'graduating'}
        />
      ) : (
        <TokenTable
          tokens={filteredTokens}
          onSelect={handleSearch}
          showProgress={activeTab === 'graduating'}
        />
      )}

      {!loadingFeed && filteredTokens.length === 0 && (
        <div style={{
          textAlign: 'center',
          padding: '40px 20px',
          color: 'var(--text-tertiary)',
          fontSize: '13px',
        }}>
          {activeFilterCount > 0 ? 'No tokens match your filters' : 'No tokens found'}
        </div>
      )}
    </div>
  );
}

// === Sub Components ===

function StatItem({ label, value, accent }: { label: string; value: string; accent?: boolean }) {
  return (
    <div style={{ textAlign: 'center' }}>
      <div style={{
        fontSize: '11px',
        color: 'var(--text-tertiary)',
        textTransform: 'uppercase',
        letterSpacing: '0.05em',
        marginBottom: '2px',
      }}>
        {label}
      </div>
      <div style={{
        fontSize: '14px',
        fontWeight: 600,
        fontFamily: 'var(--font-mono)',
        color: accent ? 'var(--accent)' : 'var(--text-primary)',
      }}>
        {value}
      </div>
    </div>
  );
}

function SearchResultItem({ token, onClick }: { token: TrendingToken; onClick: () => void }) {
  return (
    <button
      onClick={onClick}
      style={{
        width: '100%',
        display: 'flex',
        alignItems: 'center',
        gap: '10px',
        padding: '10px 12px',
        background: 'transparent',
        border: 'none',
        borderBottom: '1px solid var(--border)',
        cursor: 'pointer',
        textAlign: 'left',
      }}
      onMouseEnter={(e) => e.currentTarget.style.background = 'var(--bg-tertiary)'}
      onMouseLeave={(e) => e.currentTarget.style.background = 'transparent'}
    >
      <TokenAvatar token={token} size={28} />
      <div style={{ flex: 1, minWidth: 0 }}>
        <span style={{ fontSize: '13px', fontWeight: 600 }}>{token.symbol}</span>
        <span style={{ fontSize: '11px', color: 'var(--text-tertiary)', marginLeft: '6px' }}>{token.name}</span>
      </div>
      {token.marketCap && (
        <span style={{ fontSize: '11px', color: 'var(--text-secondary)', fontFamily: 'var(--font-mono)' }}>
          {formatMarketCap(token.marketCap)}
        </span>
      )}
      <RiskDot level={token.riskLevel} score={token.riskScore} />
    </button>
  );
}

function TabButton({ active, onClick, children }: { active: boolean; onClick: () => void; children: React.ReactNode }) {
  return (
    <button
      onClick={onClick}
      style={{
        padding: '10px 16px',
        fontSize: '12px',
        fontWeight: 500,
        fontFamily: 'var(--font-mono)',
        color: active ? 'var(--accent)' : 'var(--text-tertiary)',
        background: 'transparent',
        border: 'none',
        borderBottom: active ? '2px solid var(--accent)' : '2px solid transparent',
        cursor: 'pointer',
        marginBottom: '-1px',
      }}
    >
      {children}
    </button>
  );
}

function ViewToggleButton({ active, onClick, children }: { active: boolean; onClick: () => void; children: React.ReactNode }) {
  return (
    <button
      onClick={onClick}
      style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        width: '32px',
        height: '28px',
        background: active ? 'var(--accent-muted)' : 'transparent',
        border: 'none',
        color: active ? 'var(--accent)' : 'var(--text-tertiary)',
        cursor: 'pointer',
      }}
    >
      {children}
    </button>
  );
}

function FiltersPanel({
  marketCapIndex, setMarketCapIndex,
  ageIndex, setAgeIndex,
  selectedRisks, setSelectedRisks,
  requireSocials, setRequireSocials,
  requireLpBurned, setRequireLpBurned,
  requireMintRevoked, setRequireMintRevoked,
  onClear, activeCount,
}: {
  marketCapIndex: number;
  setMarketCapIndex: (i: number) => void;
  ageIndex: number;
  setAgeIndex: (i: number) => void;
  selectedRisks: RiskLevel[];
  setSelectedRisks: (r: RiskLevel[]) => void;
  requireSocials: boolean;
  setRequireSocials: (v: boolean) => void;
  requireLpBurned: boolean;
  setRequireLpBurned: (v: boolean) => void;
  requireMintRevoked: boolean;
  setRequireMintRevoked: (v: boolean) => void;
  onClear: () => void;
  activeCount: number;
}) {
  const toggleRisk = (risk: RiskLevel) => {
    if (selectedRisks.includes(risk)) {
      setSelectedRisks(selectedRisks.filter(r => r !== risk));
    } else {
      setSelectedRisks([...selectedRisks, risk]);
    }
  };

  return (
    <div style={{
      padding: '16px',
      marginBottom: '16px',
      background: 'var(--bg-secondary)',
      border: '1px solid var(--border)',
      borderRadius: 'var(--radius-md)',
    }}>
      <div style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
        gap: '16px',
      }}>
        {/* Market Cap */}
        <FilterSection label="Market Cap">
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px' }}>
            {MARKET_CAP_OPTIONS.map((opt, i) => (
              <FilterChip key={opt.label} active={marketCapIndex === i} onClick={() => setMarketCapIndex(i)}>
                {opt.label}
              </FilterChip>
            ))}
          </div>
        </FilterSection>

        {/* Age */}
        <FilterSection label="Age">
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px' }}>
            {AGE_OPTIONS.map((opt, i) => (
              <FilterChip key={opt.label} active={ageIndex === i} onClick={() => setAgeIndex(i)}>
                {opt.label}
              </FilterChip>
            ))}
          </div>
        </FilterSection>

        {/* Risk Level */}
        <FilterSection label="Risk Level">
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px' }}>
            {RISK_OPTIONS.map((opt) => (
              <FilterChip
                key={opt.value}
                active={selectedRisks.includes(opt.value)}
                onClick={() => toggleRisk(opt.value)}
                color={getRiskColor(opt.value)}
              >
                {opt.label}
              </FilterChip>
            ))}
          </div>
        </FilterSection>

        {/* Safety */}
        <FilterSection label="Safety">
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px' }}>
            <FilterChip active={requireSocials} onClick={() => setRequireSocials(!requireSocials)}>
              Has Socials
            </FilterChip>
            <FilterChip active={requireLpBurned} onClick={() => setRequireLpBurned(!requireLpBurned)}>
              LP Burned
            </FilterChip>
            <FilterChip active={requireMintRevoked} onClick={() => setRequireMintRevoked(!requireMintRevoked)}>
              Mint Revoked
            </FilterChip>
          </div>
        </FilterSection>
      </div>

      {activeCount > 0 && (
        <button
          onClick={onClear}
          style={{
            marginTop: '12px',
            padding: '6px 12px',
            background: 'transparent',
            border: '1px solid var(--border)',
            borderRadius: 'var(--radius-sm)',
            color: 'var(--text-tertiary)',
            fontSize: '11px',
            fontFamily: 'var(--font-mono)',
            cursor: 'pointer',
          }}
        >
          Clear all filters
        </button>
      )}
    </div>
  );
}

function FilterSection({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div>
      <div style={{
        fontSize: '10px',
        fontWeight: 600,
        textTransform: 'uppercase',
        letterSpacing: '0.05em',
        color: 'var(--text-tertiary)',
        marginBottom: '8px',
      }}>
        {label}
      </div>
      {children}
    </div>
  );
}

function FilterChip({ active, onClick, children, color }: { active: boolean; onClick: () => void; children: React.ReactNode; color?: string }) {
  return (
    <button
      onClick={onClick}
      style={{
        padding: '5px 10px',
        fontSize: '11px',
        fontFamily: 'var(--font-mono)',
        background: active ? (color ? `${color}20` : 'var(--accent-muted)') : 'transparent',
        border: '1px solid',
        borderColor: active ? (color || 'var(--accent)') : 'var(--border)',
        borderRadius: 'var(--radius-sm)',
        color: active ? (color || 'var(--accent)') : 'var(--text-secondary)',
        cursor: 'pointer',
      }}
    >
      {children}
    </button>
  );
}

function LoadingGrid({ viewMode }: { viewMode: ViewMode }) {
  if (viewMode === 'table') {
    return (
      <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
        {[1, 2, 3, 4, 5].map((i) => (
          <SkeletonTableRow key={i} />
        ))}
      </div>
    );
  }
  return (
    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))', gap: '10px' }}>
      {[1, 2, 3, 4, 5, 6, 7, 8].map((i) => (
        <SkeletonCard key={i} />
      ))}
    </div>
  );
}

function SkeletonCard() {
  return (
    <div style={{
      padding: '14px',
      background: 'var(--bg-secondary)',
      border: '1px solid var(--border)',
      borderRadius: 'var(--radius-md)',
    }}>
      {/* Header: avatar + risk */}
      <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: '10px' }}>
        <SkeletonBox width={40} height={40} rounded />
        <SkeletonBox width={32} height={16} />
      </div>

      {/* Name */}
      <div style={{ marginBottom: '6px' }}>
        <SkeletonBox width={80} height={18} style={{ marginBottom: '4px' }} />
        <SkeletonBox width={120} height={12} />
      </div>

      {/* Market cap */}
      <div style={{ display: 'flex', gap: '8px', marginBottom: '6px' }}>
        <SkeletonBox width={60} height={16} />
        <SkeletonBox width={40} height={14} />
      </div>

      {/* Stats */}
      <div style={{ display: 'flex', gap: '12px', marginBottom: '8px' }}>
        <SkeletonBox width={70} height={12} />
        <SkeletonBox width={60} height={12} />
      </div>

      {/* Safety badges */}
      <div style={{ display: 'flex', gap: '4px', marginBottom: '8px' }}>
        <SkeletonBox width={28} height={16} />
        <SkeletonBox width={36} height={16} />
        <SkeletonBox width={44} height={16} />
      </div>

      {/* Footer */}
      <div style={{ display: 'flex', justifyContent: 'space-between' }}>
        <SkeletonBox width={50} height={10} />
        <div style={{ display: 'flex', gap: '4px' }}>
          <SkeletonBox width={16} height={16} />
          <SkeletonBox width={16} height={16} />
        </div>
      </div>
    </div>
  );
}

function SkeletonTableRow() {
  return (
    <div style={{
      display: 'flex',
      alignItems: 'center',
      gap: '16px',
      padding: '12px',
      background: 'var(--bg-secondary)',
      borderRadius: 'var(--radius-sm)',
    }}>
      <SkeletonBox width={28} height={28} rounded />
      <SkeletonBox width={80} height={14} />
      <SkeletonBox width={60} height={12} style={{ marginLeft: 'auto' }} />
      <SkeletonBox width={50} height={12} />
      <SkeletonBox width={70} height={12} />
      <SkeletonBox width={40} height={12} />
      <SkeletonBox width={24} height={16} />
    </div>
  );
}

function SkeletonBox({ width, height, rounded, style }: { width: number; height: number; rounded?: boolean; style?: React.CSSProperties }) {
  return (
    <div style={{
      width,
      height,
      background: 'var(--bg-tertiary)',
      borderRadius: rounded ? '50%' : 'var(--radius-sm)',
      animation: 'pulse 1.5s ease-in-out infinite',
      ...style,
    }} />
  );
}

function TokenGrid({ tokens, onSelect, showProgress }: { tokens: (TrendingToken & { bondingProgress?: number })[]; onSelect: (address: string) => void; showProgress?: boolean }) {
  return (
    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))', gap: '10px' }}>
      {tokens.map((token) => (
        <TokenCard key={token.address} token={token} onClick={() => onSelect(token.address)} showProgress={showProgress} />
      ))}
    </div>
  );
}

function TokenCard({ token, onClick, showProgress }: { token: TrendingToken & { bondingProgress?: number }; onClick: () => void; showProgress?: boolean }) {
  return (
    <button
      onClick={onClick}
      style={{
        display: 'flex',
        flexDirection: 'column',
        padding: '14px',
        background: 'var(--bg-secondary)',
        border: '1px solid var(--border)',
        borderRadius: 'var(--radius-md)',
        cursor: 'pointer',
        textAlign: 'left',
        transition: 'border-color 0.15s, background 0.15s',
      }}
      onMouseEnter={(e) => {
        e.currentTarget.style.borderColor = 'var(--accent)';
        e.currentTarget.style.background = 'var(--bg-tertiary)';
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.borderColor = 'var(--border)';
        e.currentTarget.style.background = 'var(--bg-secondary)';
      }}
    >
      {/* Header: avatar + risk */}
      <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: '10px' }}>
        <TokenAvatar token={token} size={40} />
        <RiskDot level={token.riskLevel} score={token.riskScore} />
      </div>

      {/* Name */}
      <div style={{ marginBottom: '6px' }}>
        <div style={{ fontSize: '15px', fontWeight: 600, color: 'var(--text-primary)' }}>{token.symbol}</div>
        <div style={{ fontSize: '11px', color: 'var(--text-tertiary)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
          {token.name}
        </div>
      </div>

      {/* Market cap + price change */}
      <div style={{ display: 'flex', alignItems: 'baseline', gap: '8px', marginBottom: '6px' }}>
        {token.marketCap && (
          <span style={{ fontSize: '14px', fontWeight: 600, fontFamily: 'var(--font-mono)', color: 'var(--accent)' }}>
            {formatMarketCap(token.marketCap)}
          </span>
        )}
        {token.priceChange24h !== undefined && (
          <span style={{
            fontSize: '11px',
            fontFamily: 'var(--font-mono)',
            color: token.priceChange24h >= 0 ? 'var(--success)' : 'var(--error)',
          }}>
            {token.priceChange24h >= 0 ? '+' : ''}{token.priceChange24h.toFixed(1)}%
          </span>
        )}
      </div>

      {/* Stats row */}
      <div style={{ display: 'flex', gap: '12px', fontSize: '11px', color: 'var(--text-tertiary)', marginBottom: '8px' }}>
        {token.holderCount !== undefined && (
          <span>{formatNumber(token.holderCount, 0)} holders</span>
        )}
        {token.volume24h !== undefined && (
          <span>Vol {formatMarketCap(token.volume24h)}</span>
        )}
      </div>

      {/* Safety badges */}
      <div style={{ display: 'flex', gap: '4px', flexWrap: 'wrap', marginBottom: '8px' }}>
        {token.lpBurned && <SafetyBadge label="LP" safe />}
        {token.mintRevoked && <SafetyBadge label="MINT" safe />}
        {token.freezeRevoked && <SafetyBadge label="FREEZE" safe />}
        {!token.lpBurned && <SafetyBadge label="LP" safe={false} />}
        {!token.mintRevoked && <SafetyBadge label="MINT" safe={false} />}
      </div>

      {/* Footer: time + socials */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginTop: 'auto' }}>
        {token.createdAt && (
          <span style={{ fontSize: '10px', color: 'var(--text-tertiary)', fontFamily: 'var(--font-mono)' }}>
            {formatTimeAgo(token.createdAt)}
          </span>
        )}
        <div style={{ display: 'flex', gap: '4px' }}>
          {token.hasTwitter && <SocialIcon type="twitter" />}
          {token.hasTelegram && <SocialIcon type="telegram" />}
          {token.hasWebsite && <SocialIcon type="website" />}
        </div>
      </div>

      {/* Bonding progress */}
      {showProgress && token.bondingProgress !== undefined && (
        <div style={{ marginTop: '10px' }}>
          <div style={{ width: '100%', height: '4px', background: 'var(--bg-primary)', borderRadius: '2px', overflow: 'hidden' }}>
            <div style={{
              width: `${token.bondingProgress}%`,
              height: '100%',
              background: token.bondingProgress >= 95 ? 'var(--success)' : 'var(--accent)',
            }} />
          </div>
          <div style={{ fontSize: '10px', color: token.bondingProgress >= 95 ? 'var(--success)' : 'var(--text-tertiary)', textAlign: 'right', marginTop: '2px', fontFamily: 'var(--font-mono)' }}>
            {token.bondingProgress}%
          </div>
        </div>
      )}
    </button>
  );
}

function TokenTable({ tokens, onSelect, showProgress }: { tokens: (TrendingToken & { bondingProgress?: number })[]; onSelect: (address: string) => void; showProgress?: boolean }) {
  return (
    <div style={{ overflowX: 'auto' }}>
      <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '12px' }}>
        <thead>
          <tr style={{ borderBottom: '1px solid var(--border)' }}>
            <th style={{ ...thStyle, textAlign: 'left' }}>Token</th>
            <th style={thStyle}>Price</th>
            <th style={thStyle}>24h</th>
            <th style={thStyle}>MCap</th>
            <th style={thStyle}>Holders</th>
            <th style={thStyle}>Volume</th>
            <th style={thStyle}>Safety</th>
            <th style={thStyle}>Risk</th>
            {showProgress && <th style={thStyle}>Progress</th>}
            <th style={thStyle}>Age</th>
          </tr>
        </thead>
        <tbody>
          {tokens.map((token) => (
            <tr
              key={token.address}
              onClick={() => onSelect(token.address)}
              style={{ borderBottom: '1px solid var(--border)', cursor: 'pointer' }}
              onMouseEnter={(e) => e.currentTarget.style.background = 'var(--bg-secondary)'}
              onMouseLeave={(e) => e.currentTarget.style.background = 'transparent'}
            >
              <td style={{ ...tdStyle, textAlign: 'left' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <TokenAvatar token={token} size={24} />
                  <div>
                    <div style={{ fontWeight: 600 }}>{token.symbol}</div>
                    <div style={{ fontSize: '10px', color: 'var(--text-tertiary)' }}>{token.name}</div>
                  </div>
                </div>
              </td>
              <td style={tdStyle}>-</td>
              <td style={{ ...tdStyle, color: (token.priceChange24h ?? 0) >= 0 ? 'var(--success)' : 'var(--error)' }}>
                {token.priceChange24h !== undefined ? `${token.priceChange24h >= 0 ? '+' : ''}${token.priceChange24h.toFixed(1)}%` : '-'}
              </td>
              <td style={tdStyle}>{token.marketCap ? formatMarketCap(token.marketCap) : '-'}</td>
              <td style={tdStyle}>{token.holderCount ? formatNumber(token.holderCount, 0) : '-'}</td>
              <td style={tdStyle}>{token.volume24h ? formatMarketCap(token.volume24h) : '-'}</td>
              <td style={tdStyle}>
                <div style={{ display: 'flex', gap: '2px', justifyContent: 'center' }}>
                  {token.lpBurned && <SafetyDot safe />}
                  {token.mintRevoked && <SafetyDot safe />}
                  {token.freezeRevoked && <SafetyDot safe />}
                </div>
              </td>
              <td style={tdStyle}>
                <RiskDot level={token.riskLevel} score={token.riskScore} />
              </td>
              {showProgress && (
                <td style={tdStyle}>
                  {token.bondingProgress !== undefined ? `${token.bondingProgress}%` : '-'}
                </td>
              )}
              <td style={tdStyle}>{token.createdAt ? formatTimeAgo(token.createdAt) : '-'}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

const thStyle: React.CSSProperties = {
  padding: '10px 8px',
  textAlign: 'center',
  fontWeight: 500,
  color: 'var(--text-tertiary)',
  fontFamily: 'var(--font-mono)',
  textTransform: 'uppercase',
  fontSize: '10px',
  letterSpacing: '0.05em',
};

const tdStyle: React.CSSProperties = {
  padding: '12px 8px',
  textAlign: 'center',
  fontFamily: 'var(--font-mono)',
};

// === Utility Components ===

function TokenAvatar({ token, size }: { token: TrendingToken; size: number }) {
  const [imgError, setImgError] = useState(false);

  if (token.image && !imgError) {
    return (
      <img
        src={token.image}
        alt={token.symbol}
        style={{ width: size, height: size, borderRadius: '50%', objectFit: 'cover' }}
        onError={() => setImgError(true)}
      />
    );
  }

  const hue = token.symbol.split('').reduce((acc, char) => acc + char.charCodeAt(0), 0) % 360;

  return (
    <div style={{
      width: size,
      height: size,
      borderRadius: '50%',
      background: `hsl(${hue}, 50%, 25%)`,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      fontSize: size * 0.4,
      fontWeight: 700,
      color: `hsl(${hue}, 50%, 70%)`,
    }}>
      {token.symbol.charAt(0)}
    </div>
  );
}

function RiskDot({ level, score }: { level: string; score?: number }) {
  const color = getRiskColor(level);

  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
      {score !== undefined && (
        <span style={{ fontSize: '10px', fontFamily: 'var(--font-mono)', color }}>{score}</span>
      )}
      <div style={{ width: '8px', height: '8px', borderRadius: '50%', background: color }} />
    </div>
  );
}

function SafetyBadge({ label, safe }: { label: string; safe: boolean }) {
  return (
    <span style={{
      padding: '2px 5px',
      fontSize: '9px',
      fontFamily: 'var(--font-mono)',
      fontWeight: 600,
      borderRadius: '2px',
      background: safe ? 'rgba(0, 255, 157, 0.1)' : 'rgba(255, 45, 85, 0.1)',
      color: safe ? 'var(--success)' : 'var(--error)',
      border: `1px solid ${safe ? 'rgba(0, 255, 157, 0.2)' : 'rgba(255, 45, 85, 0.2)'}`,
    }}>
      {label}
    </span>
  );
}

function SafetyDot({ safe }: { safe: boolean }) {
  return (
    <div style={{
      width: '6px',
      height: '6px',
      borderRadius: '50%',
      background: safe ? 'var(--success)' : 'var(--error)',
    }} />
  );
}

function SocialIcon({ type }: { type: 'twitter' | 'telegram' | 'website' }) {
  const icons = {
    twitter: <path d="M23 3a10.9 10.9 0 0 1-3.14 1.53 4.48 4.48 0 0 0-7.86 3v1A10.66 10.66 0 0 1 3 4s-4 9 5 13a11.64 11.64 0 0 1-7 2c9 5 20 0 20-11.5a4.5 4.5 0 0 0-.08-.83A7.72 7.72 0 0 0 23 3z" />,
    telegram: <path d="M22 2L11 13M22 2l-7 20-4-9-9-4 20-7z" />,
    website: <><circle cx="12" cy="12" r="10" /><path d="M2 12h20M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z" /></>,
  };

  return (
    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="var(--text-tertiary)" strokeWidth="2">
      {icons[type]}
    </svg>
  );
}

function getRiskColor(level: string): string {
  const colors: Record<string, string> = {
    low: 'var(--success)',
    medium: 'var(--warning)',
    high: 'var(--risk-high)',
    critical: 'var(--error)',
  };
  return colors[level] || 'var(--text-tertiary)';
}

// === Icons ===

function FilterIcon() {
  return (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <path d="M22 3H2l8 9.46V19l4 2v-8.54L22 3z" />
    </svg>
  );
}

function GridIcon() {
  return (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <rect x="3" y="3" width="7" height="7" />
      <rect x="14" y="3" width="7" height="7" />
      <rect x="3" y="14" width="7" height="7" />
      <rect x="14" y="14" width="7" height="7" />
    </svg>
  );
}

function TableIcon() {
  return (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <path d="M3 6h18M3 12h18M3 18h18" />
    </svg>
  );
}

// === Hero Section Components ===

function FeaturePill({ icon, title, desc }: { icon: React.ReactNode; title: string; desc: string }) {
  return (
    <div style={{
      display: 'flex',
      alignItems: 'flex-start',
      gap: '10px',
      padding: '12px',
      background: 'var(--bg-tertiary)',
      border: '1px solid var(--border)',
      borderRadius: 'var(--radius-md)',
    }}>
      <span style={{ color: 'var(--accent)', flexShrink: 0, marginTop: '1px' }}>{icon}</span>
      <div>
        <div style={{
          fontSize: '13px',
          fontWeight: 600,
          color: 'var(--text-primary)',
          marginBottom: '2px',
        }}>
          {title}
        </div>
        <div style={{
          fontSize: '11px',
          color: 'var(--text-tertiary)',
          lineHeight: 1.4,
        }}>
          {desc}
        </div>
      </div>
    </div>
  );
}

function ShieldIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
      <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/>
    </svg>
  );
}

function SimIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
      <rect x="2" y="3" width="20" height="14" rx="2"/>
      <path d="M8 21h8M12 17v4"/>
    </svg>
  );
}

function RadarIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
      <circle cx="12" cy="12" r="10"/>
      <path d="M12 2a10 10 0 0 1 10 10"/>
      <path d="M12 2a10 10 0 0 1 6 5"/>
      <circle cx="12" cy="12" r="2" fill="currentColor"/>
    </svg>
  );
}

function KeyIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
      <path d="M21 2l-2 2m-7.61 7.61a5.5 5.5 0 1 1-7.78 7.78 5.5 5.5 0 0 1 7.78-7.78zm0 0L15.5 7.5m0 0l3 3L22 7l-3-3m-3.5 3.5L19 4"/>
    </svg>
  );
}
